import React, {useEffect, useState} from 'react';
import {UserDto} from "../../../../../modules/account/dto/UserDto";
import {FormattedMessage, useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {UserApi} from "../../../../../modules/account/api/UserApi";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Switch,
    TextField
} from "@mui/material";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import {ValidateUtils} from "../../../../../utils/ValidateUtils";
import {UserStatus} from "../../../../../modules/account/enums/UserStatus";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import DataGroupSelectDialog from "../../../dataGroup/manage/components/DataGroupSelectDialog";
import FormFieldText from "../../../../../components/FormFieldText";
import SearchIconButton from "../../../../../components/buttons/SearchIconButton";
import {DataGroupDto} from "../../../../../modules/account/dto/DataGroupDto";
import {DataGroupApi} from "../../../../../modules/account/api/DataGroupApi";
import CloseIconButton from '../../../../../components/buttons/CloseIconButton';
import DepartmentSelectDialog from "../../../department/manage/components/DepartmentSelectDialog";
import {DepartmentDto} from "../../../../../modules/account/dto/DepartmentDto";

export type UserDetailDialogProps = {
    open: boolean;
    user: UserDto;
    onClose: () => void;
    onOk: () => void;
    departments: Array<DepartmentDto>;
    departmentMap: Map<string, DepartmentDto>;
};

const UserDetailDialog: React.FC<UserDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.user.id ? 'ACCOUNT_USER_MODIFY' : 'ACCOUNT_USER_ADD';

    const [innerUser, setInnerUser] = useState<UserDto>(props.user);

    // 数据组
    const [dataGroups, setDataGroups] = useState<Array<DataGroupDto>>([]);
    const [dataGroupMap, setDataGroupMap] = useState(new Map<string, DataGroupDto>());

    const [openSelectDepartmentDialog, setOpenSelectDepartmentDialog] = useState(false);
    const [openDataGroupSelectDialog, setOpenDataGroupSelectDialog] = useState(false);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    useEffect(() => {
        // 初始化获取一次数据组
        _fetchDataGroups();
    }, []);

    useEffect(() => {
        setInnerUser({...props.user});
    }, [props.user]);

    const _fetchDataGroups = async () => {
        const responseResult = await DataGroupApi.getDataGroupList();
        if (responseResult && responseResult.status === HttpStatus.SUCCESS) {

            // 构建数据组的Map
            const dataGroupMap = new Map<string, DataGroupDto>();
            for (let dataGroup of responseResult.data) {
                dataGroupMap.set(dataGroup.id, dataGroup);
            }
            setDataGroupMap(dataGroupMap);

            setDataGroups(DataGroupApi.constructDataGroupTree(responseResult.data));
        }
    };

    const _handleChange = (name: string, value: any) => {
        // setState是异步，多次设置要采用
        // https://legacy.reactjs.org/docs/hooks-reference.html#functional-updates
        setInnerUser(previousInnerUser => ({...previousInnerUser, [name]: value}));
        if (name === 'supervisor') {
            setInnerUser(previousInnerUser => ({...previousInnerUser, 'supervisorId': value.id}));
        }
    };

    const _handleOk = async () => {
        if (_validate()) {
            if (props.user.id) {
                console.log(innerUser);
                const responseResult = await UserApi.updateUser(innerUser);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'ACCOUNT_USER_MODIFY_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await UserApi.createUser(innerUser);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'ACCOUNT_USER_ADD_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const _handleCancel = () => {
        setMessageMap(new Map<string, string>());
        props.onClose();
    };

    const _validate = () => {
        const newMessageMap = new Map<string, string>();

        if (innerUser.uid === '') {
            newMessageMap.set('uid', intl.formatMessage({id: 'ACCOUNT_USER_UID_REQUIRED'}));
        }
        if (innerUser.id === '') {
            // 创建用户的时候需要验证密码必填项
            if (innerUser.password === '') {
                newMessageMap.set('password', intl.formatMessage({id: 'ACCOUNT_USER_PASSWORD_REQUIRED'}));
            } else if (innerUser.password.length < 8 || !ValidateUtils.isPasswordStrength(innerUser.password)) {
                newMessageMap.set("password", intl.formatMessage({id: "ACCOUNT_USER_PASSWORD_STRENGTH_REQUIRED"}));
            }
        } else if (innerUser.id !== '' && innerUser.password && innerUser.password !== '') {
            // 修改用户的时候，填写了密码需要验证密码必填项
            if (innerUser.password.length < 8 || !ValidateUtils.isPasswordStrength(innerUser.password)) {
                newMessageMap.set("password", intl.formatMessage({id: "ACCOUNT_USER_PASSWORD_STRENGTH_REQUIRED"}));
            }
        }

        if (innerUser.lastName === '') {
            newMessageMap.set('lastName', intl.formatMessage({id: 'ACCOUNT_USER_LAST_NAME_REQUIRED'}));
        }
        if (innerUser.firstName === undefined || innerUser.firstName === '') {
            newMessageMap.set('firstName', intl.formatMessage({id: 'ACCOUNT_USER_FIRST_NAME_REQUIRED'}));
        }
        // if (innerUser.mobile === '') {
        //     newMessageMap.set('mobile', intl.formatMessage({id: 'ACCOUNT_USER_MOBILE_REQUIRED'}));
        // } else if (!ValidateUtils.isMobileNumber(innerUser.mobile)) {
        //     newMessageMap.set('mobile', intl.formatMessage({id: 'ACCOUNT_USER_MOBILE_FORMAT_WRONG'}));
        // }
        // if (innerUser.email === '') {
        //     newMessageMap.set('email', intl.formatMessage({id: 'ACCOUNT_USER_EMAIL_REQUIRED'}));
        // } else if (!ValidateUtils.isEmail(innerUser.email)) {
        //     newMessageMap.set('email', intl.formatMessage({id: 'ACCOUNT_USER_EMAIL_FORMAT_WRONG'}));
        // }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={_handleCancel}>
            <DepartmentSelectDialog departments={props.departments} open={openSelectDepartmentDialog}
                                    onSelect={(value) => _handleChange("departmentId", value)}
                                    onClose={() => setOpenSelectDepartmentDialog(false)}/>
            <DataGroupSelectDialog dataGroups={dataGroups} open={openDataGroupSelectDialog}
                                   onSelect={(value) => _handleChange("dataGroupId", value)}
                                   onClose={() => setOpenDataGroupSelectDialog(false)}/>
            <DialogTitle>{intl.formatMessage({id: title})}</DialogTitle>
            <DialogContent>
                <TextField
                    variant="standard" margin={'normal'} fullWidth
                    label={intl.formatMessage({id: "ACCOUNT_USER_UID"})}
                    required={true}
                    error={messageMap.has("uid")}
                    helperText={messageMap.get("uid")}
                    value={innerUser.uid}
                    onChange={(e) => {
                        _handleChange("uid", e.target.value)
                    }}
                />
                <FormControl required={true} variant="standard" fullWidth margin="normal">
                    <InputLabel>
                        {
                            innerUser.id === '' ?
                                <FormattedMessage id={'ACCOUNT_USER_PASSWORD'}/> :
                                <FormattedMessage id={'ACCOUNT_USER_PASSWORD_RESET'}/>
                        }
                    </InputLabel>
                    <Input
                        placeholder={intl.formatMessage({id: "ACCOUNT_USER_PASSWORD_REQUIRED"})}
                        type={showPassword ? 'text' : 'password'}
                        error={messageMap.has("password")}
                        value={innerUser.password}
                        onChange={(e) => {
                            _handleChange("password", e.target.value)
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end">
                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    {messageMap.has("password") ? <FormHelperText
                        error={messageMap.has("password")}>{messageMap.get("password")}</FormHelperText> : null}
                </FormControl>
                <TextField
                    variant="standard" margin={'normal'} fullWidth
                    label={intl.formatMessage({id: "ACCOUNT_USER_LAST_NAME"})}
                    required={true}
                    error={messageMap.has("lastName")}
                    helperText={messageMap.get("lastName")}
                    value={innerUser.lastName}
                    onChange={(e) => {
                        _handleChange("lastName", e.target.value)
                    }}
                />

                <TextField
                    variant="standard" margin={'normal'} fullWidth
                    label={intl.formatMessage({id: "ACCOUNT_USER_FIRST_NAME"})}
                    required={true}
                    error={messageMap.has("firstName")}
                    helperText={messageMap.get("firstName")}
                    value={innerUser.firstName}
                    onChange={(e) => {
                        _handleChange("firstName", e.target.value)
                    }}
                />

                {/*<TextField*/}
                {/*    variant="standard" margin={'normal'} fullWidth*/}
                {/*    required={true}*/}
                {/*    error={messageMap.has("email")}*/}
                {/*    helperText={messageMap.get("email")}*/}
                {/*    label={intl.formatMessage({id: "ACCOUNT_USER_EMAIL"})}*/}
                {/*    value={innerUser.email}*/}
                {/*    onChange={(e) => {*/}
                {/*        handleChange("email", e.target.value)*/}
                {/*    }}*/}
                {/*/>*/}

                {/*<TextField*/}
                {/*    variant="standard" margin={'normal'} fullWidth*/}
                {/*    label={intl.formatMessage({id: "ACCOUNT_USER_MOBILE"})}*/}
                {/*    value={innerUser.mobile}*/}
                {/*    required={true}*/}
                {/*    error={messageMap.has("mobile")}*/}
                {/*    helperText={messageMap.get("mobile")}*/}
                {/*    onChange={(e) => {*/}
                {/*        handleChange("mobile", e.target.value)*/}
                {/*    }}*/}
                {/*/>*/}
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_USER_DEPARTMENT"})}
                               value={innerUser.departmentId && props.departmentMap.has(innerUser.departmentId) ? props.departmentMap.get(innerUser.departmentId)?.name : ''}
                               disabled={true}
                               endAdornment={
                                   <div>
                                       <SearchIconButton onClick={() => setOpenSelectDepartmentDialog(true)}/>
                                       <CloseIconButton onClick={() => _handleChange("departmentId", "")}/>
                                   </div>
                               }/>

                <FormControl variant="standard" margin={'normal'}>
                    <FormLabel>
                        <FormattedMessage id={'ACCOUNT_USER_STATUS'}/>
                    </FormLabel>
                    <FormControlLabel
                        control={
                            <Switch checked={(innerUser.status === UserStatus.Enum.Active)}
                                    onChange={(e) => {
                                        _handleChange('status', e.target.checked ? UserStatus.Enum.Active : UserStatus.Enum.Inactive)
                                    }}/>
                        }
                        label={innerUser.status === UserStatus.Enum.Active ?
                            <FormattedMessage id={'ACCOUNT_USER_STATUS_ACTIVE'}/> :
                            <FormattedMessage id={'ACCOUNT_USER_STATUS_INACTIVE'}/>}/>
                </FormControl>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_USER_DATA_GROUP"})}
                               value={innerUser.dataGroupId && dataGroupMap.get(innerUser.dataGroupId) ? dataGroupMap.get(innerUser.dataGroupId)?.name : ""}
                               disabled={true}
                               endAdornment={
                                   <div>
                                       <SearchIconButton onClick={() => setOpenDataGroupSelectDialog(true)}/>
                                       <CloseIconButton onClick={() => _handleChange("dataGroupId", "")}/>
                                   </div>
                               }/>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={_handleCancel} color="secondary">
                    <FormattedMessage id={'COMMON_CANCEL'}/>
                </Button>
                <Button variant="outlined" onClick={_handleOk} color="primary">
                    <FormattedMessage id={'COMMON_OK'}/>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserDetailDialog;
